import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { UserPlus, Mail, Phone, Check, ArrowRight, ArrowLeft, School, BookOpen } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import PhoneInput, { getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// Safe localStorage wrapper
const safeStorage = {
  getItem: function(key) { try { return localStorage.getItem(key); } catch(e) { return null; } },
  setItem: function(key, value) { try { localStorage.setItem(key, value); } catch(e) {} },
  removeItem: function(key) { try { localStorage.removeItem(key); } catch(e) {} }
};

const GRADES = [
  { value: 'Sec1 (Grade 7)', label: 'Sec1 (Grade 7)' },
  { value: 'Sec2 (Grade 8)', label: 'Sec2 (Grade 8)' },
  { value: 'Sec3 (Grade 9)', label: 'Sec3 (Grade 9)' },
  { value: 'Sec4 (Grade 10)', label: 'Sec4 (Grade 10)' },
  { value: 'IB (Grade 11)', label: 'IB (Grade 11)' },
  { value: 'IB (Grade 12)', label: 'IB (Grade 12)' },
  { value: 'Others', label: 'Others' }
];

const SUBJECTS = ['Physics', 'Mathematics', 'Chemistry', 'Biology'];
const COURSE_TYPES = ['1-on-1 Tutoring', 'Small Group (2-4)', 'Crash Course', 'Assessment Only'];

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState({ email: false, phone: false });
  const [courses, setCourses] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country_code: '',
    country: '',
    school_name: '',
    grade: '',
    grade_other: '',
    subjects: [],
    course_type: '',
    parent_contact: '',
    notes: ''
  });
  
  // OTP states - Production (no dev OTP display)
  const [emailOtp, setEmailOtp] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState({ email: false, phone: false });
  const [otpCooldown, setOtpCooldown] = useState({ email: 0, phone: 0 });

  useEffect(function() {
    axios.get(API + "/public/courses").then(function(res) { setCourses(res.data); }).catch(function() {});
  }, []);

  // Cooldown timer
  useEffect(function() {
    var interval = setInterval(function() {
      setOtpCooldown(function(prev) {
        return {
          email: Math.max(0, prev.email - 1),
          phone: Math.max(0, prev.phone - 1)
        };
      });
    }, 1000);
    return function() { clearInterval(interval); };
  }, []);

  var sendOtp = async function(type) {
    if (otpCooldown[type] > 0) {
      toast.error("Please wait " + otpCooldown[type] + " seconds before requesting another OTP");
      return;
    }

    setSendingOtp(function(prev) { return { ...prev, [type]: true }; });
    
    try {
      var payload = type === 'email' 
        ? { email: formData.email, type: 'email' }
        : { phone: formData.phone, type: 'phone' };
      
      await axios.post(API + "/otp/send", payload);
      setOtpSent(function(prev) { return { ...prev, [type]: true }; });
      setOtpCooldown(function(prev) { return { ...prev, [type]: 60 }; }); // 60 second cooldown
      toast.success("OTP sent to your " + type + ". Please check and enter the code.");
    } catch (err) {
      var message = err.response && err.response.data && err.response.data.detail 
        ? err.response.data.detail 
        : err.message;
      toast.error("Failed to send OTP: " + message);
    } finally {
      setSendingOtp(function(prev) { return { ...prev, [type]: false }; });
    }
  };

  var verifyOtp = async function(type) {
    try {
      var payload = type === 'email'
        ? { email: formData.email, otp: emailOtp, type: 'email' }
        : { phone: formData.phone, otp: phoneOtp, type: 'phone' };
      
      await axios.post(API + "/otp/verify", payload);
      
      if (type === 'email') {
        setEmailVerified(true);
        toast.success('Email verified successfully!');
      } else {
        setPhoneVerified(true);
        toast.success('Phone verified successfully!');
      }
    } catch (err) {
      var message = err.response && err.response.data && err.response.data.detail 
        ? err.response.data.detail 
        : err.message;
      toast.error("Verification failed: " + message);
    }
  };

  var handleSubjectToggle = function(subject) {
    setFormData(function(prev) {
      return {
        ...prev,
        subjects: prev.subjects.includes(subject)
          ? prev.subjects.filter(function(s) { return s !== subject; })
          : [...prev.subjects, subject]
      };
    });
  };

  var handleSubmit = async function() {
    if (!emailVerified) {
      toast.error('Please verify your email before submitting');
      return;
    }

    setLoading(true);
    try {
      var res = await axios.post(API + "/students/register", formData);
      toast.success('Registration successful!');
      safeStorage.setItem('studentId', res.data.student_id);
      safeStorage.setItem('studentEmail', formData.email);
      safeStorage.setItem('studentName', formData.full_name);
      navigate('/payment');
    } catch (err) {
      var message = err.response && err.response.data && err.response.data.detail 
        ? err.response.data.detail 
        : err.message;
      toast.error("Registration failed: " + message);
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = formData.full_name && formData.email && formData.phone && isValidPhoneNumber(formData.phone || '');
  const canProceedStep2 = emailVerified; // Only email verification required for now
  const canProceedStep3 = formData.school_name && formData.grade && formData.subjects.length > 0 && formData.course_type;

  return (
    <div className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-2xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
            <UserPlus className="w-4 h-4 text-electric-cyan" />
            <span className="text-electric-cyan text-sm font-medium">Join PhysicsCo</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-4" data-testid="register-title">
            Register Now
          </h1>
          <p className="text-slate-400">Complete the form below to start your learning journey</p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                step >= s ? 'bg-electric-cyan text-deep-blue' : 'bg-surface-navy text-slate-400'
              }`}>
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 3 && <div className={`w-16 h-1 rounded ${step > s ? 'bg-electric-cyan' : 'bg-surface-navy'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass-card p-8"
          >
            <h2 className="font-outfit text-xl font-semibold text-white mb-6">Personal Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Full Name *</label>
                <Input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Enter your full name"
                  className="input-dark"
                  data-testid="register-name"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  className="input-dark"
                  data-testid="register-email"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">Phone Number *</label>
                <div className="phone-input-container">
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry="SG"
                    value={formData.phone}
                    onChange={(value) => {
                      setFormData({ 
                        ...formData, 
                        phone: value || ''
                      });
                    }}
                    onCountryChange={(country) => {
                      if (country) {
                        try {
                          const callingCode = getCountryCallingCode(country);
                          setFormData(prev => ({
                            ...prev,
                            country_code: '+' + callingCode,
                            country: country
                          }));
                        } catch (e) {
                          // Country code not available
                        }
                      }
                    }}
                    className="input-dark"
                    data-testid="register-phone"
                  />
                </div>
                {formData.phone && !isValidPhoneNumber(formData.phone) && (
                  <p className="text-red-400 text-xs mt-1">Please enter a valid phone number</p>
                )}
              </div>
            </div>

            <Button
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
              className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
              data-testid="register-next-1"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass-card p-8"
          >
            <h2 className="font-outfit text-xl font-semibold text-white mb-6">Verify Your Contact</h2>
            
            {/* Email OTP */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5 text-electric-cyan" />
                <span className="text-white font-medium">Email Verification</span>
                {emailVerified && <Check className="w-5 h-5 text-green-400" />}
              </div>
              
              {!emailVerified ? (
                <>
                  {!otpSent.email ? (
                    <Button
                      onClick={function() { sendOtp('email'); }}
                      disabled={sendingOtp.email}
                      className="btn-secondary w-full"
                      data-testid="send-email-otp"
                    >
                      {sendingOtp.email ? 'Sending...' : 'Send OTP to ' + formData.email}
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-slate-400">Enter the 6-digit code sent to your email</p>
                      <div className="flex justify-center">
                        <InputOTP maxLength={6} value={emailOtp} onChange={setEmailOtp}>
                          <InputOTPGroup>
                            {[0, 1, 2, 3, 4, 5].map(function(i) { return (
                              <InputOTPSlot key={i} index={i} className="bg-deep-navy border-surface-navy" />
                            ); })}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      <Button
                        onClick={function() { verifyOtp('email'); }}
                        disabled={emailOtp.length !== 6}
                        className="btn-primary w-full"
                        data-testid="verify-email-otp"
                      >
                        Verify Email
                      </Button>
                      <button 
                        onClick={function() { sendOtp('email'); }}
                        disabled={otpCooldown.email > 0}
                        className="text-sm text-slate-400 hover:text-electric-cyan disabled:opacity-50 w-full text-center"
                      >
                        {otpCooldown.email > 0 ? 'Resend in ' + otpCooldown.email + 's' : 'Resend OTP'}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-green-400 text-sm">Email verified successfully</p>
              )}
            </div>

            {/* Phone OTP - Optional for now */}
            <div className="mb-8 opacity-60">
              <div className="flex items-center gap-2 mb-4">
                <Phone className="w-5 h-5 text-slate-500" />
                <span className="text-slate-400 font-medium">Phone Verification</span>
                <span className="text-xs bg-slate-700 px-2 py-0.5 rounded text-slate-400">Coming Soon</span>
              </div>
              <p className="text-sm text-slate-500">Phone verification will be available soon. Only email verification is required for registration.</p>
            </div>

            <div className="flex gap-4">
              <Button onClick={function() { setStep(1); }} className="btn-secondary flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={function() { setStep(3); }}
                disabled={!canProceedStep2}
                className="btn-primary flex-1"
                data-testid="register-next-2"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Academic Info */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass-card p-8"
          >
            <h2 className="font-outfit text-xl font-semibold text-white mb-6">Academic Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">School Name *</label>
                <Input
                  type="text"
                  value={formData.school_name}
                  onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                  placeholder="Enter your school name"
                  className="input-dark"
                  data-testid="register-school"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">Grade/Level *</label>
                <Select value={formData.grade} onValueChange={(v) => setFormData({ ...formData, grade: v })}>
                  <SelectTrigger className="input-dark" data-testid="register-grade">
                    <SelectValue placeholder="Select your grade" />
                  </SelectTrigger>
                  <SelectContent className="bg-deep-navy border-surface-navy">
                    {GRADES.map(grade => (
                      <SelectItem key={grade.value} value={grade.value} className="text-white hover:bg-surface-navy">
                        {grade.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.grade === 'Others' && (
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Specify Syllabus & Grade *</label>
                  <Input
                    type="text"
                    value={formData.grade_other}
                    onChange={(e) => setFormData({ ...formData, grade_other: e.target.value })}
                    placeholder="E.g., AP Physics Year 2"
                    className="input-dark"
                    data-testid="register-grade-other"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">Subjects *</label>
                <div className="grid grid-cols-2 gap-3">
                  {SUBJECTS.map(subject => (
                    <div
                      key={subject}
                      onClick={() => handleSubjectToggle(subject)}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-2 ${
                        formData.subjects.includes(subject)
                          ? 'bg-electric-cyan/20 border-electric-cyan text-white'
                          : 'bg-surface-navy/50 border-surface-navy text-slate-400 hover:border-slate-500'
                      }`}
                      data-testid={`register-subject-${subject.toLowerCase()}`}
                    >
                      <Checkbox checked={formData.subjects.includes(subject)} />
                      {subject}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Course Type *</label>
                <Select value={formData.course_type} onValueChange={(v) => setFormData({ ...formData, course_type: v })}>
                  <SelectTrigger className="input-dark" data-testid="register-course-type">
                    <SelectValue placeholder="Select course type" />
                  </SelectTrigger>
                  <SelectContent className="bg-deep-navy border-surface-navy">
                    {COURSE_TYPES.map(type => (
                      <SelectItem key={type} value={type} className="text-white hover:bg-surface-navy">
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Parent Contact (Optional)</label>
                <Input
                  type="tel"
                  value={formData.parent_contact}
                  onChange={(e) => setFormData({ ...formData, parent_contact: e.target.value })}
                  placeholder="+65 XXXX XXXX"
                  className="input-dark"
                  data-testid="register-parent"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Additional Notes (Optional)</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any specific requirements or questions?"
                  className="input-dark resize-none"
                  rows={3}
                  data-testid="register-notes"
                />
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <Button onClick={() => setStep(2)} className="btn-secondary flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canProceedStep3 || loading}
                className="btn-primary flex-1"
                data-testid="register-submit"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-deep-blue"></div>
                ) : (
                  <>Complete Registration <Check className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Register;
